import constants from '../../../../../../appConstants';
import { IBETARenderer } from '@dataGrid/components/cellRenderer';
import {
  customAlphanumericComparator,
  formatDate,
  formatDateTime
} from '@dataGrid/gridUtilities';
import {
  MultiSelectFilter,
  DateTimeFilter
} from '@dataGrid/components/columnFilters';
import { formatAddress } from '../../../../Dispatch/utils/utils';
import {
  ibEtaValueFormatter,
  getAPTWindowConfig
} from '../../../../IBPlanning/ShipmentsView/utils';

class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = 'GridConfiguration';
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration({ name, terminalAccessLabel, columnConfig }) {
    return GridConfig.getDefaultConfiguration({
      name,
      terminalAccessLabel,
      columnConfig
    });
  }

  static getProNumberClasses(params) {
    return ['gridCell-leftAlign', params.context?.hasProImagesURL ? 'actionable-cell' : ''];
  }

  static getAddressValue(params, type) {
    const prefix = type === 'customer' ? 'customer' : 'shipper';
    return formatAddress({
      address: params.data?.[`${prefix}Address`],
      address2: params.data?.[`${prefix}Address2`],
      city: params.data?.[`${prefix}City`],
      state: params.data?.[`${prefix}State`],
      zip: params.data?.[`${prefix}Zip`],
      country: params.data?.[`${prefix}Country`]
    });
  }

  static getDefaultConfiguration({ name, terminalAccessLabel, columnConfig }) {
    const aptConfig = getAPTWindowConfig(columnConfig);
    return {
      title: name,
      keyColumn: 'proId',
      rowSelection: 'multiple',
      rowDeselection: 'true',
      columns: [
        {
          headerName: 'Pro#',
          field: 'proId',
          headerTooltip: 'Pro# ID',
          minWidth: constants.columnWidth.PROID,
          cellClass: GridConfig.getProNumberClasses,
          filter: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING
          }
        },
        {
          headerName: `Orig ${terminalAccessLabel.suffix}`,
          headerTooltip: `Origin ${terminalAccessLabel.fullName}`,
          field: 'originTerminal',
          minWidth: constants.columnWidth.TERMINAL,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Dest Terminal',
          headerTooltip: 'Destination Terminal',
          field: 'destTerminal',
          cellClass: 'gridCell-leftAlign',
          minWidth: constants.columnWidth.W_140,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Customer #',
          field: 'displayCustomerCode',
          headerTooltip: 'Customer Code',
          minWidth: constants.columnWidth.W_125,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Customer',
          headerTooltip: 'Customer Name',
          field: 'displayCustomerName',
          minWidth: constants.columnWidth.CUSTOMER,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          filterParams: {
            width: constants.columnWidth.W_300
          }
        },
        {
          headerName: 'Address',
          headerTooltip: 'Customer Address',
          minWidth: constants.columnWidth.W_240,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipValueGetter: (params) => params.value,
          valueGetter: (params) => GridConfig.getAddressValue(params, 'customer'),
          filterParams: {
            width: constants.columnWidth.W_300
          }
        },
        {
          headerName: 'Shipper #',
          field: 'shipperCode',
          headerTooltip: 'Shipper Code',
          minWidth: constants.columnWidth.W_115,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Shipper',
          field: 'shipperName',
          headerTooltip: 'Shipper Name',
          minWidth: constants.columnWidth.CUSTOMER,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipField: 'shipperName',
          filterParams: {
            width: constants.columnWidth.W_300
          }
        },
        {
          headerName: 'Shipper Address',
          headerTooltip: 'Shipper Address',
          minWidth: constants.columnWidth.W_240,
          cellClass: 'gridCell-leftAlign',
          valueGetter: (params) => GridConfig.getAddressValue(params, 'shipper'),
          filter: MultiSelectFilter,
          tooltipValueGetter: (params) => params.value,
          filterParams: {
            width: constants.columnWidth.W_300
          }
        },
        {
          headerName: 'Due Date',
          headerTooltip: 'Due Date',
          field: 'dueDate',
          minWidth: constants.columnWidth.DUE_DATE,
          cellClass: 'gridCell-leftAlign',
          valueFormatter: formatDate,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATE
          }
        },
        {
          headerName: 'IB ETA',
          headerTooltip: 'IB ETA',
          field: 'inboundArrivalTime',
          minWidth: constants.columnWidth.IBETA,
          valueFormatter: ibEtaValueFormatter,
          cellRendererFramework: IBETARenderer,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME
          }
        },
        {
          headerName: 'Bay Location',
          headerTooltip: 'Bay Location',
          field: 'bayLocation',
          cellClass: 'gridCell-leftAlign',
          minWidth: constants.columnWidth.W_130,
          filter: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING
          },
          comparator: customAlphanumericComparator
        },
        {
          headerName: 'Weight',
          headerTooltip: 'Weight',
          field: 'weight',
          minWidth: constants.columnWidth.WEIGHT,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Pallet Count',
          headerTooltip: 'Pallet Count',
          field: 'palletCount',
          minWidth: constants.columnWidth.PALLET_COUNT,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },

        {
          headerName: 'Curr Mfst',
          headerTooltip: 'Manifest ID',
          field: 'manifestNumber',
          minWidth: constants.columnWidth.MANIFEST_ID,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: aptConfig.headerName,
          headerTooltip: aptConfig.headerTooltip,
          field: 'shipmentCTA',
          minWidth: constants.columnWidth.EARLY_LATE_CTA,
          cellClass: 'gridCell-leftAlign',
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          },
          valueGetter: aptConfig.valueGetter,
          valueFormatter: aptConfig.valueFormatter,
          tooltipValueGetter: (params) => params.value
        },
        {
          headerName: 'Pickup Date',
          headerTooltip: 'Pickup Date',
          field: 'pickupDate',
          minWidth: constants.columnWidth.DUE_DATE,
          cellClass: 'gridCell-leftAlign',
          valueFormatter: formatDate,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATE
          }
        },
        {
          headerName: 'ILINE',
          headerTooltip: 'ILINE',
          field: 'iLine',
          minWidth: constants.columnWidth.ILINE,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Last Updated (UTC)',
          headerTooltip: 'Last Updated (UTC)',
          field: 'lastUpdated',
          minWidth: constants.columnWidth.W_180,
          cellClass: 'gridCell-leftAlign',
          filter: DateTimeFilter,
          valueFormatter: formatDateTime,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME
          }
        },
        {
          headerName: 'Source',
          headerTooltip: 'Source',
          field: 'holdProDetails.source',
          minWidth: constants.columnWidth.W_110,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Last Modified By',
          field: 'holdProDetails.lastModifiedBy',
          minWidth: constants.columnWidth.W_200,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          filterParams: {
            width: constants.columnWidth.W_200
          }
        },
        {
          headerName: 'User Modified On (UTC)',
          field: 'holdProDetails.lastModifiedDateTS',
          minWidth: constants.columnWidth.W_180,
          cellClass: 'gridCell-leftAlign',
          valueFormatter: formatDateTime,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME
          }
        }
      ]
    };
  }
}
export default GridConfig;
